import { Filter as FilterIcon } from "lucide-react";
import * as React from "react";

import { RobotoButton } from "@/shared/foundation/primitives/RobotoButton";
import { usePopupState } from "@/shared/hooks/usePopupState";

import type { FilterDefinition } from "./FilterDefinition";
import { FilterMenu } from "./FilterMenu";
import type {
  ActiveFilters,
  FilterMenuOutput,
  RoboqlMenuConfig,
} from "./FilterModel";

export interface FiltersProps {
  /**
   * Filterable fields available in the Properties pane. The component
   * does not fetch schema or data itself — the consumer is responsible
   * for assembling this list (typically a static set for built-in
   * fields plus a dynamic set for org-specific custom fields).
   */
  filterDefinitions: FilterDefinition[];

  /**
   * Fired when the user commits a filter. Emits `FilterMenuOutput` so
   * a single handler can route both structured filters and roboql
   * queries — typically `view.commit` from `useResourceView`.
   */
  onFilterSelected?: (output: FilterMenuOutput) => void;

  /**
   * Current committed filter state. Required when `roboql` is provided
   * so the menu can pre-fill the editor and render the conflict
   * banner. Optional when only the Properties pane is wired.
   */
  filters?: ActiveFilters;

  /**
   * When provided, the menu surfaces the RoboQL mode bar + editor.
   * When absent, the menu renders today's Properties-only UI — so
   * existing consumers without a roboql dependency are unaffected.
   */
  roboql?: RoboqlMenuConfig;

  /**
   * When true, the menu surfaces a Metrics tab (a searchable tree that fetches
   * the org's metric definitions itself).
   */
  metrics?: boolean;
}

/**
 * Labeled "Filter" outlined button in the Actions row. Opens the shared
 * `<FilterMenu>` popover (also reached via the inline `+` button in
 * `<ResourceView.ActiveFilters>`); the menu lets the user enter exactly
 * one filter at a time and emits it via `onFilterSelected`. When a
 * `roboql` config is supplied the menu also surfaces a RoboQL pane via
 * the mode bar; without it, only the Properties pane renders.
 */
export function Filters({
  filterDefinitions,
  onFilterSelected,
  filters,
  roboql,
  metrics,
}: FiltersProps) {
  const popup = usePopupState();

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    if (popup.isOpen) {
      popup.close();
    } else {
      popup.open({ anchorEl: event.currentTarget });
    }
  };

  return (
    <>
      <RobotoButton
        variant="secondary"
        icon={<FilterIcon aria-hidden="true" />}
        onClick={handleClick}
        aria-haspopup="dialog"
        aria-expanded={popup.isOpen}
      >
        Filter
      </RobotoButton>
      <FilterMenu
        open={popup.isOpen}
        anchorEl={popup.anchor}
        onClose={popup.close}
        filterDefinitions={filterDefinitions}
        onCommit={(output) => onFilterSelected?.(output)}
        filters={filters}
        roboql={roboql}
        metrics={metrics}
      />
    </>
  );
}
